/**
 * Mental Map — lab page.
 *
 * Two things live here: opening a live room (a prompt becomes a join link that
 * anyone can open with just a name), and a preview of the drawing canvas,
 * legend, examples and presentation stage against a simulated room.
 */

import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';

import { TextInput } from '@/components/ds';
import { Button } from '@/components/ui/Button';
import { createMentalMapRoom, listMyMentalMapRooms, mentalMapJoinUrl, type MyRoom } from '@/features/mental-map/liveRoom';
import { MentalMapRoom } from '@/features/mental-map/MentalMapRoom';
import { EMPTY_MAP, mapLevel } from '@/features/mental-map/model';
import { usePreviewRoom } from '@/features/mental-map/previewRoom';
import { listSavedModels, listSaveSpaces, savedModelPath, type SavedModel, type SaveSpace } from '@/features/mental-map/savedModels';
import { SaveToSpace } from '@/features/mental-map/SaveToSpace';
import { isAuthenticated } from '@/lib/supabaseSync';
import { clearTenantContext } from '@/lib/tenantContext';
import { useUserStore } from '@/stores/prioritization/userStore';

const DEFAULT_PROMPT = 'How do you see this problem?';

function OpenRoom() {
  const [prompt, setPrompt] = useState(DEFAULT_PROMPT);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [joinUrl, setJoinUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [signedOut, setSignedOut] = useState(false);
  const navigate = useNavigate();
  const [rooms, setRooms] = useState<MyRoom[]>([]);
  const [showRooms, setShowRooms] = useState(false);

  const loadRooms = useCallback(async () => {
    const result = await listMyMentalMapRooms();
    if (result.ok && result.data) setRooms(result.data);
  }, []);
  useEffect(() => {
    void loadRooms();
  }, [loadRooms]);
  const signOut = useUserStore((st) => st.signOut);

  // The same three steps as the header's sign-out button.
  const signInAgain = () => {
    clearTenantContext();
    signOut();
    navigate('/auth/login', { replace: true, state: { from: { pathname: '/elevation/lab/mental-map' } } });
  };

  const open = async (event: FormEvent) => {
    event.preventDefault();
    if (!prompt.trim() || busy) return;
    setBusy(true);
    setError(null);
    // The app can show this page from a remembered identity while the actual
    // sign-in has lapsed (or belongs to another environment); the room would
    // then be refused as anonymous. Say so plainly instead.
    if (!(await isAuthenticated())) {
      setBusy(false);
      setSignedOut(true);
      setError('You are not signed in here any more, so the room cannot be opened under your name.');
      return;
    }
    const result = await createMentalMapRoom(prompt.trim());
    setBusy(false);
    if (!result.ok || !result.data) {
      setError(result.error ?? 'The room could not be opened.');
      return;
    }
    setJoinUrl(mentalMapJoinUrl(window.location.origin, result.data.joinToken));
    void loadRooms();
  };

  const copy = async () => {
    if (!joinUrl) return;
    try {
      await navigator.clipboard.writeText(joinUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setError('Could not copy — select the link text instead.');
    }
  };

  return (
    <div className="border-b border-[var(--rule)] bg-[var(--bg-raised)] px-4 py-3">
      {joinUrl ? (
        <div className="flex flex-wrap items-center gap-3">
          <p className="font-body text-sm text-[var(--text-sec)]">Room open. Anyone with this link joins with just a name:</p>
          <code className="min-w-0 break-all rounded-md bg-[var(--surface)] px-2 py-1 font-mono text-xs text-[var(--text-pri)]">{joinUrl}</code>
          <Button variant="secondary" size="sm" onClick={() => void copy()}>
            {copied ? 'Copied' : 'Copy link'}
          </Button>
          <Button variant="cta" size="sm" onClick={() => window.open(joinUrl, '_blank', 'noopener')}>
            Open the room
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setJoinUrl(null)}>
            Open another
          </Button>
        </div>
      ) : (
        <form className="flex flex-wrap items-end gap-3" onSubmit={(e) => void open(e)}>
          <label className="min-w-64 flex-1 font-body text-sm text-[var(--text-sec)]" htmlFor="mental-map-prompt">
            Open a live room — what should everyone map?
            <TextInput
              id="mental-map-prompt"
              className="mt-1"
              value={prompt}
              maxLength={200}
              onChange={(e) => setPrompt(e.target.value)}
            />
          </label>
          <Button type="submit" variant="cta" disabled={!prompt.trim() || busy}>
            {busy ? 'Opening…' : 'Open a room'}
          </Button>
        </form>
      )}
      {rooms.length > 0 && (
        <div className="mt-2">
          <Button variant="ghost" size="sm" onClick={() => setShowRooms(!showRooms)}>
            {showRooms ? 'Hide your rooms' : `Your rooms (${rooms.length})`}
          </Button>
          {showRooms && (
            <ul className="mt-1 max-h-48 space-y-1 overflow-y-auto">
              {rooms.map((room) => (
                <li key={room.joinToken} className="flex flex-wrap items-center gap-3 rounded-md px-2 py-1 hover:bg-[var(--surface)]">
                  <span className="min-w-0 flex-1 truncate font-body text-sm text-[var(--text-pri)]">{room.prompt}</span>
                  <span className="font-mono text-xs text-[var(--text-dim)]">{new Date(room.createdAt).toLocaleDateString()}</span>
                  <Button variant="secondary" size="sm" onClick={() => window.open(mentalMapJoinUrl(window.location.origin, room.joinToken), '_blank', 'noopener')}>
                    Open
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => void navigator.clipboard.writeText(mentalMapJoinUrl(window.location.origin, room.joinToken)).catch(() => undefined)}
                  >
                    Copy link
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      {error && (
        <p className="mt-2 flex flex-wrap items-center gap-3 font-body text-sm text-[var(--coral)]">
          {error}
          {signedOut && (
            <Button variant="cta" size="sm" onClick={signInAgain}>
              Sign in again
            </Button>
          )}
        </p>
      )}
    </div>
  );
}

/** The models you have kept, grouped by the space they live in. */
function SavedModels() {
  const navigate = useNavigate();
  const [models, setModels] = useState<SavedModel[]>([]);
  const [spaces, setSpaces] = useState<SaveSpace[]>([]);
  const [creating, setCreating] = useState(false);
  const [open, setOpen] = useState(true);

  useEffect(() => {
    let cancelled = false;
    void Promise.all([listSavedModels(), listSaveSpaces()]).then(([found, mine]) => {
      if (cancelled) return;
      if (found.ok && found.data) setModels(found.data);
      if (mine.ok && mine.data) setSpaces(mine.data);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const groups = spaces
    .map((space) => ({ space, models: models.filter((m) => m.spaceId === space.id) }))
    .filter((g) => g.models.length > 0);

  return (
    <div className="border-b border-[var(--rule)] bg-[var(--bg-raised)] px-4 py-3">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="font-heading text-base font-semibold text-[var(--text-pri)]">Your saved models</h2>
        <span className="font-body text-sm text-[var(--text-dim)]">
          {models.length === 0 ? 'None yet. Draw one here, or save one from a room.' : `${models.length} kept in your spaces`}
        </span>
        <span className="flex-1" />
        {models.length > 0 && (
          <Button variant="ghost" size="sm" onClick={() => setOpen(!open)}>
            {open ? 'Hide' : 'Show'}
          </Button>
        )}
        <Button variant="cta" size="sm" onClick={() => setCreating(true)}>
          New model
        </Button>
      </div>
      {open && groups.length > 0 && (
        <div className="mt-2 max-h-56 space-y-3 overflow-y-auto">
          {groups.map(({ space, models: inSpace }) => (
            <section key={space.id}>
              <p className="font-mono text-xs uppercase tracking-wider text-[var(--text-dim)]">
                {space.name} · {space.kind === 'personal' ? 'only you' : 'team'}
              </p>
              <ul className="mt-1 space-y-1">
                {inSpace.map((model) => (
                  <li key={model.id} className="flex flex-wrap items-center gap-3 rounded-md px-2 py-1 hover:bg-[var(--surface)]">
                    <span className="min-w-0 flex-1 truncate font-body text-sm text-[var(--text-pri)]">{model.name}</span>
                    {model.level && (
                      <span className="font-mono text-xs text-[var(--text-dim)]">
                        Level {model.level} · {mapLevel(model.level).name}
                      </span>
                    )}
                    <span className="font-mono text-xs text-[var(--text-dim)]">{model.updatedAt ? new Date(model.updatedAt).toLocaleDateString() : ''}</span>
                    <Button variant="secondary" size="sm" onClick={() => navigate(savedModelPath(model.id))}>
                      Open
                    </Button>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}
      <SaveToSpace
        open={creating}
        onClose={() => setCreating(false)}
        doc={EMPTY_MAP}
        defaultName=""
        onSaved={(id) => navigate(savedModelPath(id))}
      />
    </div>
  );
}

export function MentalMapLab() {
  const room = usePreviewRoom(DEFAULT_PROMPT);
  return (
    <div className="flex h-[calc(100vh-4rem)] min-h-[560px] flex-col">
      <OpenRoom />
      <SavedModels />
      <p className="border-b border-[var(--rule)] bg-[var(--bg)] px-4 py-1.5 font-body text-xs text-[var(--text-sec)]">
        Below: a preview with five simulated people. Nothing you draw in the preview is saved or shared.
      </p>
      <div className="min-h-0 flex-1">
        <MentalMapRoom room={room} />
      </div>
    </div>
  );
}
